import React from "react";
import { CodeBlock } from "./Codeblock";
import {
  MdWhatsapp,
  MdInfo,
  MdDescription,
  MdSend,
  MdCampaign,
  MdImage,
} from "react-icons/md";

// ─── Base URL ─────────────────────────────────────────────────────────

const BASE_URL_SAMPLE = `https://apiwhat.empedance.com`;

const AUTH_SAMPLE = `curl 'https://apiwhat.empedance.com/api/whatsapp/company/send/search?limit=10' \\
  -H 'Authorization: Bearer <your_token>' \\
  -H 'Accept: application/json'`;

// ─── Component ────────────────────────────────────────────────────────

export const OverviewSection: React.FC = () => {
  return (
    <div className="space-y-8">

      {/* Hero */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shadow-sm">
            <MdWhatsapp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp API</h1>
            <p className="text-xs text-green-600 font-medium">
              Powered by Meta Cloud API
            </p>
          </div>
        </div>

        <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
          The Empedance WhatsApp API lets you send template messages, manage
          contacts, and run bulk campaigns — all through a simple REST interface
          backed by the{" "}
          <span className="font-medium text-gray-800">
            Meta WhatsApp Business Cloud API
          </span>.
          No direct Meta credentials needed; authenticate once with your
          Empedance token and you're ready.
        </p>
      </div>

      {/* Meta connection callout */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex gap-3">
        <div className="text-blue-500 mt-0.5">
          <MdInfo className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm font-semibold text-blue-800 mb-1">
            Connected to Meta
          </p>
          <p className="text-xs text-blue-700 leading-relaxed">
            This API is a managed wrapper around the{" "}
            <span className="font-medium">
              Meta WhatsApp Business Cloud API (v22.0)
            </span>.
            All templates require Meta approval before they can be sent. Message
            delivery, read receipts, and template status updates are handled
            through Meta's infrastructure.
          </p>
        </div>
      </div>

      {/* Key capabilities */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-3">
          What you can do
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            {
              icon: <MdDescription className="w-6 h-6 text-gray-700" />,
              title: "Manage Templates",
              desc: "Create, list, and track approval status of WhatsApp message templates.",
            },
            {
              icon: <MdSend className="w-6 h-6 text-gray-700" />,
              title: "Send Single Messages",
              desc: "Send an approved template to one recipient by phone number.",
            },
            {
              icon: <MdCampaign className="w-6 h-6 text-gray-700" />,
              title: "Bulk Campaigns",
              desc: "Dispatch templates to thousands of contacts via customer IDs, phone numbers, or CSV upload.",
            },
            {
              icon: <MdImage className="w-6 h-6 text-gray-700" />,
              title: "Rich Media",
              desc: "Attach images, videos, and documents as template headers using our media upload endpoint.",
            },
          ].map(({ icon, title, desc }) => (
            <div
              key={title}
              className="flex gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl"
            >
              <div>{icon}</div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{title}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                  {desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Base URL */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-2">
          Base URL
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          All API endpoints are relative to the following base URL:
        </p>
        <CodeBlock
          title="Base URL"
          variants={[{ label: "URL", value: BASE_URL_SAMPLE }]}
        />
      </div>

      {/* Authentication quick look */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-2">
          Authentication
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          Every request must include a{" "}
          <code className="bg-gray-100 px-1 rounded text-gray-700">
            Bearer
          </code>{" "}
          token in the{" "}
          <code className="bg-gray-100 px-1 rounded text-gray-700">
            Authorization
          </code>{" "}
          header. See the{" "}
          <span className="text-blue-600 font-medium">Authentication</span>{" "}
          section for details on obtaining your token.
        </p>
        <CodeBlock
          title="Example Request"
          variants={[{ label: "cURL", value: AUTH_SAMPLE }]}
        />
      </div>

      {/* Request / Response format */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-3">
          Request & Response Format
        </h2>
        <div className="space-y-2 text-xs text-gray-600">
          {[
            [
              "Content-Type",
              "JSON endpoints accept application/json. Bulk send uses multipart/form-data.",
            ],
            [
              "Response envelope",
              "Every response wraps data in { status, data, error }. status: 1 means success.",
            ],
            [
              "Errors",
              "Non-2xx responses include a human-readable error string in the error field.",
            ],
            [
              "Pagination",
              "List endpoints return a paging.cursors.after value — pass it as ?after= to fetch the next page.",
            ],
          ].map(([label, desc]) => (
            <div
              key={label}
              className="flex gap-2 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg"
            >
              <span className="font-semibold text-gray-700 whitespace-nowrap">
                {label}:
              </span>
              <span>{desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
        <p className="font-semibold mb-2">Quick start</p>
        <ol className="list-decimal list-inside space-y-1 text-xs text-green-700">
          <li>
            Get your Bearer token from the{" "}
            <span className="font-medium">Authentication</span> section.
          </li>
          <li>
            Create a template via{" "}
            <span className="font-medium">Add Template</span> and wait for Meta
            approval.
          </li>
          <li>
            Send to a single number using{" "}
            <span className="font-medium">Send Single Msg Template</span>.
          </li>
          <li>
            Scale up with{" "}
            <span className="font-medium">Send Bulk Msg Template</span> using a
            CSV upload.
          </li>
        </ol>
      </div>
    </div>
  );
};